import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const DefectRate = () => {
  // State variables for Defect Rate calculation
  const [defectiveUnits, setDefectiveUnits] = useState(0); // Number of defective units
  const [totalUnits, setTotalUnits] = useState(0); // Total units produced
  const [defectRate, setDefectRate] = useState(0); // Calculated defect rate

  // Event handlers to update state values
  const handleDefectiveUnitsChange = (e) =>
    setDefectiveUnits(parseFloat(e.target.value) || 0);
  const handleTotalUnitsChange = (e) =>
    setTotalUnits(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate defect rate when any input changes
  useEffect(() => {
    if (totalUnits > 0) {
      const rate = (defectiveUnits / totalUnits) * 100;
      setDefectRate(rate);
    } else {
      setDefectRate(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [defectiveUnits, totalUnits]);

  return (
    <Layout title="Defect Rate Calculator" path="defect-rate">
      <section className="section-padding" id="defect-rate-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Defect Rate Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Defect Rate Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Defective Units Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="defectiveUnits" className="form-label">
                        Defective Units
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Defective Units:</strong> The number of units that are deemed defective or below acceptable quality standards."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="defectiveUnits"
                        placeholder="Enter number of defective units"
                        value={defectiveUnits || ""}
                        onChange={handleDefectiveUnitsChange}
                      />
                    </div>

                    {/* Total Units Produced Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalUnits" className="form-label">
                        Total Units Produced
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Units Produced:</strong> The total number of units manufactured or processed in the given period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalUnits"
                        placeholder="Enter total units produced"
                        value={totalUnits || ""}
                        onChange={handleTotalUnitsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Defect Rate:{" "}
                        <span className="fw-bold ms-3">
                          {defectRate === 0
                            ? "0.00"
                            : defectRate.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Defect Rate Calculator
                </h3>

                {/* What is a Defect Rate Calculator? */}
                <h4 className="mt-3 mb-3">What is a Defect Rate Calculator?</h4>
                <p>
                  The **Defect Rate Calculator** helps determine the percentage
                  of defective units in a production batch. It is a key metric
                  for assessing product quality and identifying areas for
                  improvement in manufacturing processes.
                </p>

                {/* Why Use a Defect Rate Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Defect Rate Calculator?</h4>
                <p>
                  This calculator is essential for quality assurance and process
                  improvement. By understanding defect rates, businesses can
                  implement measures to reduce waste and enhance production
                  efficiency.
                </p>

                {/* Benefits of Using a Defect Rate Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Defect Rate Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Identifies Quality Issues:</strong> Helps pinpoint
                    problems in production processes causing defects.
                  </li>
                  <li>
                    <strong>Improves Efficiency:</strong> Reduces waste and
                    optimizes resource utilization.
                  </li>
                  <li>
                    <strong>Supports Continuous Improvement:</strong> Provides
                    actionable insights for process refinement.
                  </li>
                  <li>
                    <strong>Enhances Customer Satisfaction:</strong> Ensures
                    high-quality products reach customers.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Defect Rate Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Manufacturing Teams:</strong> To monitor and improve
                    production quality.
                  </li>
                  <li>
                    <strong>Quality Control Experts:</strong> For assessing
                    defect rates and ensuring compliance with standards.
                  </li>
                  <li>
                    <strong>Engineering Departments:</strong> To refine
                    processes and reduce defects in production.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  What is an acceptable defect rate?
                </h4>
                <p>
                  An acceptable defect rate varies by industry and product type.
                  Generally, a lower defect rate indicates better quality.
                  Industries with high precision requirements, like aerospace,
                  aim for near-zero defect rates.
                </p>

                <h4 className="mt-4 mb-3">How can I reduce the defect rate?</h4>
                <p>
                  To reduce defect rates, focus on improving production
                  processes, training employees, maintaining equipment, and
                  implementing quality assurance programs.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is monitoring the defect rate important?
                </h4>
                <p>
                  Monitoring defect rates is crucial for maintaining
                  high-quality standards, minimizing waste, and ensuring
                  customer satisfaction. It also helps reduce costs associated
                  with defective products.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DefectRate;
